import type { APIError } from '@vizit/api';
import { Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { ClockRewindIcon, SortAlphabeticalIcon } from '@vizit/icons';
import {
  AssetTypeSelect,
  ClearableSelect,
  type ClearableSelectItem,
  ProductCategoryComboBox,
  SearchTextField,
} from '@vizit/organisms';
import { colors, cx } from '@vizit/theme';
import type * as React from 'react';
import { useCollectionGrid } from '../../providers/collection-grid-provider';
import { CollectionSortOption } from '../../providers/collection-grid-provider.state';
import { useProductCategoryList } from './collection-grid-filters.hooks';

const FILTER_MIN_WIDTH = 'min-w-[233px]';
const SORT_ICON_COLOR = colors['gray-utility']['400'];

const COLLECTION_SORT_ITEMS: ClearableSelectItem[] = [
  {
    id: CollectionSortOption.DateAdded,
    label: 'Newest',
    icon: <ClockRewindIcon size={14} stroke={SORT_ICON_COLOR} aria-hidden />,
  },
  {
    id: CollectionSortOption.Alphabetical,
    label: 'Alphabetical (A-Z)',
    icon: <SortAlphabeticalIcon size={14} className="text-gray-utility-400" aria-hidden />,
  },
];

interface CollectionGridFiltersProps extends React.HTMLAttributes<HTMLDivElement> {
  onAuthenticationError: (error: APIError) => void;
  /**
   * Shown first in the toolbar, e.g. the Collections / Benchmarks switch
   */
  leadingContent?: React.ReactNode;
}

function CollectionGridFilters(props: CollectionGridFiltersProps) {
  const { className, onAuthenticationError, leadingContent, ...other } = props;

  const {
    workspaceId,
    organizationId,
    productCategoryId,
    productCategoryName,
    imageType,
    searchTerm,
    sortOption,
    onFilterProductCategory,
    onFilterImageType,
    onSearchTermChange,
    onSortOptionChange,
  } = useCollectionGrid();

  const {
    items: productCategoryItems,
    isLoading: isLoadingProductCategory,
    filterText: productCategoryFilterText,
    setFilterText: setProductCategoryFilterText,
    loadMore: loadMoreProductCategory,
    onSelectionChange: onCategorySelectionChange,
  } = useProductCategoryList({
    organizationId,
    workspaceId,
    filterText: productCategoryName || '',
    onAuthenticationError,
    onFilterProductCategory,
  });

  return (
    <div className={cx('flex flex-row items-center justify-between gap-6 pb-8', className)} {...other}>
      <div className="flex flex-row flex-wrap items-center gap-6">
        {leadingContent}
        <SearchTextField
          className="flex-1"
          data-testid="library-collections-search"
          onSearch={onSearchTermChange}
          defaultValue={searchTerm ?? ''}
          variant="filled"
          placeholder="Search collections by name"
          inputProps={{ className: 'pr-10' }}
        />
        <ProductCategoryComboBox
          data-testid="library-collections-category-filter"
          filterText={productCategoryFilterText}
          setFilterText={setProductCategoryFilterText}
          items={productCategoryItems}
          selectedKey={productCategoryId}
          onSelectionChange={onCategorySelectionChange}
          isLoading={isLoadingProductCategory}
          loadMore={loadMoreProductCategory}
        />
        <AssetTypeSelect
          className={FILTER_MIN_WIDTH}
          data-testid="library-collections-image-type-filter"
          assetType={imageType}
          placeholder="All image types"
          onAssetTypeChange={onFilterImageType}
        />
      </div>

      <div className="flex shrink-0 items-center gap-4">
        <Text size={TextSize.Md} weight={TextWeight.Medium} color={TextColor.Primary}>
          Sort by:
        </Text>
        <ClearableSelect
          ariaLabel="Collection sort"
          placeholder="Newest"
          items={COLLECTION_SORT_ITEMS}
          selectedKey={sortOption}
          onSelectionChange={(key) => key && onSortOptionChange(key as CollectionSortOption)}
          includeAllOption={false}
          isClearable={false}
          className="min-w-[233px]"
          data-testid="library-collections-sort-filter"
        />
      </div>
    </div>
  );
}

export { CollectionGridFilters };
export type { CollectionGridFiltersProps };
export { CollectionGridFilterOptions } from '../../providers/collection-grid-provider.state';
